import { EmailOutlined, PhoneOutlined, WhatsApp } from '@mui/icons-material'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    type SelectChangeEvent
} from '@mui/material'
import React from 'react'

import type { ContactSupportData } from '@/src/constants/contactSupport'

export interface ContactFormFieldValues {
    firstName: string
    lastName: string
    email: string
    phone: string
    stdCode: string
    subject: string
    message: string
}

export interface ContactFormCopy {
    title: string
    subtitle: string
    firstName: string
    lastName: string
    emailId: string
    phoneNumber: string
    subject: string
    message: string
    selectPlaceholder: string
    reset: string
    submit: string
    customerServiceLine1: string
    customerServiceLine2: string
    callsFrom: string
    internationalWhatsapp: string
    generalInquiries: string
    officeAddressTitle: string
    contactNumber: string
    registeredNumber: string
    vat: string
}

export interface ContactFormViewProps {
    supportData: ContactSupportData
    formData: ContactFormFieldValues
    onFieldChange: (field: keyof ContactFormFieldValues, value: string) => void
    onSubmit: (e: React.FormEvent) => void
    onReset: () => void
    loading: boolean
    status: { type: 'success' | 'error'; message: string } | null
    onDismissStatus: () => void
    isRtl: boolean
    copy: ContactFormCopy
    subjectOptions: { value: string; label: string }[]
    stdCodeOptions: { value: string; label: string }[]
}

const RequiredMark = () => (
    <Typography component="span" sx={{ color: 'error.main', marginInlineStart: 0.25 }}>
        *
    </Typography>
)

export const ContactFormView: React.FC<ContactFormViewProps> = ({
    supportData,
    formData,
    onFieldChange,
    onSubmit,
    onReset,
    loading,
    status,
    onDismissStatus,
    isRtl,
    copy,
    subjectOptions,
    stdCodeOptions
}) => {
    const handleSubjectChange = (event: SelectChangeEvent<string>) => {
        onFieldChange('subject', event.target.value)
    }

    const handleStdCodeChange = (event: SelectChangeEvent<string>) => {
        onFieldChange('stdCode', event.target.value)
    }

    const officeContactDisplay = supportData.officeContactNumber ?? supportData.contactNumber

    return (
        <Box
            sx={{
                py: 3,
                flex: 1,
                bgcolor: 'grey.50',
                direction: isRtl ? 'rtl' : 'ltr'
            }}
        >
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: 'flex',
                        gap: 4,
                        // RTL flips flex “start”; row-reverse keeps form left + sidebar right like LTR
                        flexDirection: { xs: 'column', lg: isRtl ? 'row-reverse' : 'row' },
                        alignItems: 'flex-start'
                    }}
                >
                    <Box sx={{ flex: { xs: '1 1 auto', lg: 7 }, minWidth: 0, width: '100%' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                border: 1,
                                borderColor: 'borders.default'
                            }}
                        >
                            <Typography
                                variant="h4"
                                sx={theme => ({
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: theme.palette.primary.main,
                                    textAlign: 'start'
                                })}
                            >
                                {copy.title}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: 'text.secondary', mb: 3, textAlign: 'start' }}
                            >
                                {copy.subtitle}
                            </Typography>

                            {status && (
                                <Alert severity={status.type} sx={{ mb: 3 }} onClose={onDismissStatus}>
                                    {status.message}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={onSubmit} noValidate>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                        >
                                            {copy.firstName}
                                            <RequiredMark />
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={formData.firstName}
                                            onChange={e => onFieldChange('firstName', e.target.value)}
                                            required
                                            disabled={loading}
                                            variant="outlined"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                        >
                                            {copy.lastName}
                                            <RequiredMark />
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={formData.lastName}
                                            onChange={e => onFieldChange('lastName', e.target.value)}
                                            required
                                            disabled={loading}
                                            variant="outlined"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                        >
                                            {copy.emailId}
                                            <RequiredMark />
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="email"
                                            value={formData.email}
                                            onChange={e => onFieldChange('email', e.target.value)}
                                            required
                                            disabled={loading}
                                            variant="outlined"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                        >
                                            {copy.phoneNumber}
                                            <RequiredMark />
                                        </Typography>
                                        <Stack direction="row" spacing={0} useFlexGap sx={{ gap: 1 }}>
                                            <FormControl sx={{ minWidth: 120 }} variant="outlined">
                                                <Select
                                                    value={formData.stdCode}
                                                    onChange={handleStdCodeChange}
                                                    disabled={loading}
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1 }}
                                                >
                                                    {stdCodeOptions.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                fullWidth
                                                value={formData.phone}
                                                onChange={e => onFieldChange('phone', e.target.value)}
                                                required
                                                disabled={loading}
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={12}>
                                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                            {copy.subject}
                                            <RequiredMark />
                                        </Typography>
                                        <FormControl fullWidth required variant="outlined">
                                            <Select
                                                value={formData.subject}
                                                onChange={handleSubjectChange}
                                                disabled={loading}
                                                displayEmpty
                                                notched={false}
                                                inputProps={{ 'aria-label': copy.subject }}
                                                renderValue={selected =>
                                                    selected ? (
                                                        subjectOptions.find(o => o.value === selected)?.label ?? selected
                                                    ) : (
                                                        <Typography component="span" sx={{ color: 'text.disabled' }}>
                                                            {copy.selectPlaceholder}
                                                        </Typography>
                                                    )
                                                }
                                                sx={{ borderRadius: 1 }}
                                            >
                                                <MenuItem value="">
                                                    <em>{copy.selectPlaceholder}</em>
                                                </MenuItem>
                                                {subjectOptions.map(opt => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={12}>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                        >
                                            {copy.message}
                                            <RequiredMark />
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            maxRows={4}
                                            value={formData.message}
                                            onChange={e => onFieldChange('message', e.target.value)}
                                            required
                                            disabled={loading}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                    alignItems: 'flex-start'
                                                },
                                                '& .MuiInputBase-inputMultiline': {
                                                    boxSizing: 'border-box',
                                                    overflow: 'auto'
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Stack
                                            direction="row"
                                            spacing={0}
                                            useFlexGap
                                            flexWrap="wrap"
                                            sx={{ gap: 2, justifyContent: 'flex-start' }}
                                        >
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                color="primary"
                                                onClick={onReset}
                                                disabled={loading}
                                                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                                            >
                                                {copy.reset}
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="customVariantSecondary"
                                                disabled={loading}
                                                sx={{ fontWeight: 700, textTransform: 'uppercase', minWidth: 120 }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : copy.submit}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 auto', lg: 5 }, minWidth: 0, width: '100%' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 2,
                                mb: 3,
                                border: 1,
                                borderColor: 'borders.default'
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    px: 2.5,
                                    py: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexDirection: 'row'
                                }}
                            >
                                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    {supportData.workingHours}
                                </Typography>
                                <Stack spacing={0} sx={{ lineHeight: 1.2 }}>
                                    <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500 }}>
                                        {copy.customerServiceLine1}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'inherit', fontWeight: 500 }}>
                                        {copy.customerServiceLine2}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Box sx={{ bgcolor: 'grey.100', p: 2.5, textAlign: 'start' }}>
                                <Stack spacing={0} useFlexGap sx={{ gap: 2.5, width: '100%', alignItems: 'stretch' }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.secondary', mb: 0.5, textAlign: 'start' }}
                                        >
                                            {copy.callsFrom}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={0}
                                            useFlexGap
                                            alignItems="center"
                                            sx={{ gap: 1 }}
                                        >
                                            <PhoneOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                {supportData.phone}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.secondary', mb: 0.5, textAlign: 'start' }}
                                        >
                                            {copy.internationalWhatsapp}
                                        </Typography>
                                        {supportData.internationalLines?.length ? (
                                            <Stack spacing={0} useFlexGap sx={{ gap: 1 }}>
                                                {supportData.internationalLines.map((line, i) => {
                                                    const LineIcon =
                                                        line.kind === 'whatsapp' ? WhatsApp : PhoneOutlined
                                                    return (
                                                        <Stack
                                                            key={`${line.kind}-${line.value}-${i}`}
                                                            direction="row"
                                                            spacing={0}
                                                            useFlexGap
                                                            alignItems="center"
                                                            sx={{ gap: 1 }}
                                                        >
                                                            <LineIcon
                                                                sx={{ fontSize: 20, color: 'text.secondary' }}
                                                            />
                                                            <Typography
                                                                variant="body1"
                                                                sx={{ fontWeight: 700, color: 'primary.main' }}
                                                            >
                                                                {line.value}
                                                            </Typography>
                                                        </Stack>
                                                    )
                                                })}
                                            </Stack>
                                        ) : (
                                            <Stack
                                                direction="row"
                                                spacing={0}
                                                useFlexGap
                                                alignItems="center"
                                                sx={{ gap: 1 }}
                                            >
                                                <PhoneOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 700, color: 'primary.main' }}
                                                >
                                                    {supportData.contactNumber}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.secondary', mb: 0.5, textAlign: 'start' }}
                                        >
                                            {copy.generalInquiries}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={0}
                                            useFlexGap
                                            alignItems="center"
                                            sx={{ gap: 1 }}
                                        >
                                            <EmailOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                                            <Link
                                                href={`mailto:${supportData.email}`}
                                                underline="hover"
                                                sx={{ fontWeight: 700, color: 'primary.main' }}
                                            >
                                                {supportData.email}
                                            </Link>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                p: 2.5,
                                bgcolor: 'grey.100',
                                border: 1,
                                borderColor: 'borders.default',
                                textAlign: 'start'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, mb: 1, color: 'primary.main', textAlign: 'start' }}
                            >
                                {copy.officeAddressTitle}
                            </Typography>
                            {!!supportData.companyName && (
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 700, mb: 1, color: 'text.primary', textAlign: 'start' }}
                                >
                                    {supportData.companyName}
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 2,
                                    color: 'text.secondary',
                                    whiteSpace: 'pre-line',
                                    textAlign: 'start'
                                }}
                            >
                                {supportData.officeAddress}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}>
                                <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {copy.contactNumber}
                                </Typography>{' '}
                                {officeContactDisplay}
                            </Typography>
                            {!!supportData.registeredNumber && (
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, color: 'text.secondary', textAlign: 'start' }}
                                >
                                    <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {copy.registeredNumber}
                                    </Typography>{' '}
                                    {supportData.registeredNumber}
                                </Typography>
                            )}
                            {!!supportData.vat && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'start' }}>
                                    <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {copy.vat}
                                    </Typography>{' '}
                                    {supportData.vat}
                                </Typography>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}
